import type { $Fetch } from "nitropack";

export type InviteResult = 'created' | 'skipped_already_member' | 'skipped_already_invited' | 'invalid';
export type Palier = 'none' | 'connecteur' | 'ambassadeur' | 'top_connecteur';
export type ReferralInvitationStatus = 'pending' | 'accepted' | 'validated' | 'rejected';

export interface InviteOutcome {
    email: string;
    result: InviteResult;
}

// Mirrors `services::v2::referral::PassState` (api/crates/services/src/v2/referral/mod.rs).
// No `unlocked_perks` / `next_palier` fields exist on the backend struct.
export interface PassState {
    total_xp: number;
    palier: Palier;
    xp_to_next: number | null;
    xp_per_referral: number;
    thresholds: [number, number, number];
    leaderboard_opt_in: boolean;
}

// Mirrors `entities::referral::ReferralInvitation` (api/crates/core/entities/src/referral.rs).
export interface ReferralInvitation {
    invitation_id: string;
    referrer_user_id: string;
    invited_email: string;
    token: string;
    status: ReferralInvitationStatus;
    invited_user_id: string | null;
    created_at: string;
    validated_at: string | null;
    validated_by: string | null;
}

// Mirrors `repositories::v2::referral::LeaderboardRow` (api/crates/repositories/src/v2/referral/mod.rs).
export interface LeaderboardRow {
    user_id: string;
    first_name: string;
    last_name: string;
    total_xp: number;
}

export const ReferralRepository = (client: $Fetch) => ({
    /**
     * Bulk-invite emails. Backend caps batches at 100 emails and returns
     * HTTP 400 above that limit; let the error propagate naturally.
     */
    invite: (emails: string[]): Promise<InviteOutcome[]> => {
        return client(`/v2/referrals/invitations`, {
            method: 'POST',
            body: { emails },
        });
    },
    myReferrals: (): Promise<ReferralInvitation[]> => {
        return client(`/v2/referrals/me`, { method: 'GET' });
    },
    pass: (): Promise<PassState> => {
        return client(`/v2/me/pass`, { method: 'GET' });
    },
    leaderboard: (): Promise<LeaderboardRow[]> => {
        return client(`/v2/referrals/leaderboard`, { method: 'GET' });
    },
    setLeaderboardOptIn: (optIn: boolean): Promise<{ opt_in: boolean }> => {
        return client(`/v2/me/leaderboard-opt-in`, {
            method: 'PUT',
            body: { opt_in: optIn },
        });
    },
});
